import React, { useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { SCHEMAS } from 'js/api';
import { listConnectors } from 'js/actions/connector-actions';
import {
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
} from 'js/actions/team-connector-configuration-actions';
import { useQuery } from 'js/hooks/useQuery';
import { useNotify } from 'js/hooks/useNotify';
import NewConnectorConfiguration from '../components/NewConnectorConfiguration';

function NewConnectorConfigurationContainer({
  team,
  listConnectors,
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
}) {
  const { workspaceId, teamId, connectorId } = useParams();
  const history = useHistory();
  const notify = useNotify();

  // Load all connectors for the workspace
  const refreshConnectorsQuery = useCallback(
    (queryId) => listConnectors(workspaceId, queryId),
    [workspaceId, listConnectors]
  );

  const {
    result: connectors,
    refresh: refreshConnectors,
    hasLoaded: connectorsHasLoaded,
  } = useQuery({
    queryId: `connectors-${workspaceId}`,
    query: refreshConnectorsQuery,
    schema: SCHEMAS.CONNECTOR_ARRAY,
  });

  // Load all team connector configurations to identify which connectors are already configured
  const refreshTeamConnectorConfigurationsQuery = useCallback(
    (queryId) => listTeamConnectorConfigurations(teamId, queryId),
    [teamId, listTeamConnectorConfigurations]
  );

  const {
    result: teamConnectorConfigurations,
    refresh: refreshTeamConnectorConfigurations,
    hasLoaded: teamConnectorConfigurationsHasLoaded,
  } = useQuery({
    queryId: `teamConnectorConfigurations-${teamId}`,
    query: refreshTeamConnectorConfigurationsQuery,
    schema: SCHEMAS.TEAM_CONNECTOR_CONFIGURATION_ARRAY,
  });

  useEffect(() => refreshConnectors(), [refreshConnectors]);

  useEffect(
    () => refreshTeamConnectorConfigurations(),
    [refreshTeamConnectorConfigurations]
  );

  const availableConnectors = useMemo(() => {
    if (!connectorsHasLoaded || !teamConnectorConfigurationsHasLoaded) {
      return [];
    }

    const configuredConnectorIds = new Set(
      (teamConnectorConfigurations ?? []).map((config) => config.connector_id)
    );

    return (connectors ?? []).filter(
      (connector) => !configuredConnectorIds.has(connector.id)
    );
  }, [
    connectors,
    teamConnectorConfigurations,
    connectorsHasLoaded,
    teamConnectorConfigurationsHasLoaded,
  ]);

  const selectedConnector = useMemo(() => {
    if (!connectorId || !availableConnectors) {
      return null;
    }
    return availableConnectors.find(
      (connector) => connector.id === connectorId
    );
  }, [availableConnectors, connectorId]);

  const handleSaveConfiguration = useCallback(
    async (configurationData) => {
      try {
        if (!connectorId) {
          throw new Error('No connector selected');
        }

        await createTeamConnectorConfiguration(teamId, {
          connector_id: connectorId,
          configuration: configurationData,
        });
        notify({
          type: 'success',
          message: 'Connector configuration created successfully',
        });

        // Navigate back to team settings
        history.push(`/w/${workspaceId}/teams/${teamId}/settings`);
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to save connector configuration',
        });
        throw error;
      }
    },
    [
      connectorId,
      teamId,
      workspaceId,
      createTeamConnectorConfiguration,
      notify,
      history,
    ]
  );

  // Handle deleting configuration (not applicable for new configurations)
  const handleDeleteConfiguration = useCallback(async () => {
    // This shouldn't be called for new configurations
    notify({
      type: 'warning',
      message: 'Cannot delete a configuration that has not been created yet',
    });
  }, [notify]);

  // Handle cancel - navigate back to team settings
  const handleCancel = useCallback(() => {
    history.push(`/w/${workspaceId}/teams/${teamId}/settings`);
  }, [history, workspaceId, teamId]);

  return (
    <NewConnectorConfiguration
      team={team}
      availableConnectors={availableConnectors}
      selectedConnector={selectedConnector}
      onCancel={handleCancel}
      onSaveConfiguration={handleSaveConfiguration}
      onDeleteConfiguration={handleDeleteConfiguration}
      isLoading={!connectorsHasLoaded || !teamConnectorConfigurationsHasLoaded}
    />
  );
}

export default connect(null, {
  listConnectors,
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
})(NewConnectorConfigurationContainer);
